<template>
  <IntegrationWorkflowWrapper
    v-if="isSalesforceAndPartnerTagsPushEnabled"
    v-model:is-enabled="trayState.partnerAttributesPush"
    @update:is-enabled="handlePartnerTagsSyncToggled"
    title="Push Crossbeam partner tags to Salesforce"
    :description="description"
    data-testid="partner-tags-push-workflow"
    v-slot="{ isSlotDisabled }"
    :suppress-slot-error="true"
  >
    <div class="partner-tags-push-section mt-16 mb-16">
      <div>
        <label for="partnerTagsField">Partner tags:</label>
        <BittsSelect
          v-bind="baseSelectProps"
          v-model="tagsCrmField"
          id="partnerTagsField"
          :options="picklistOptions"
          :disabled="
            (isSlotDisabled ?? false) ||
            !trayState?.isEnabled ||
            !trayState.partnerAttributesPush ||
            trayState.isSaving
          "
          :error="!!tagsFieldError"
          :error-message="tagsFieldError ?? ''"
          size="medium"
        />
      </div>
    </div>
  </IntegrationWorkflowWrapper>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, inject, ref, watch } from 'vue';

import useIteratively from '@/composables/useIteratively';
import {
  getFieldErrorMessage,
  useSalesforcePicklistFieldsForPush,
} from '@/composables/useSalesforcePicklistFieldsForPush';
import { CONFIGURE_PARTNER_TAGS_PUSH_TO_SALESFORCE } from '@/constants/feature_flags';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { useFeatureFlagStore, useFeedsStore } from '@/stores';

import IntegrationWorkflowWrapper from './IntegrationWorkflowWrapper.vue';

const description =
  'Crossbeam will write partner tags to this field on mapped partner accounts.';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const { hasFeatureFlag } = useFeatureFlagStore();
const feedsStore = useFeedsStore();
const { crmFeeds } = storeToRefs(feedsStore);
const { iteratively } = useIteratively();
const isSalesforce = computed(() => trayState.profileType === 'salesforce');

const integrationCRM = computed(() => {
  const feedType =
    trayState.profileType === 'hubspot' ? 'hubspot_v3' : trayState.profileType;
  return crmFeeds.value.find((feed) => feed.integration.type === feedType);
});
const isSalesforceAndPartnerTagsPushEnabled = computed(
  () =>
    isSalesforce.value &&
    hasFeatureFlag(CONFIGURE_PARTNER_TAGS_PUSH_TO_SALESFORCE),
);

const baseSelectProps = {
  allowClear: true,
  placeholder: 'Map to CRM field',
  class: 'w-300',
};

const tagsCrmField = ref<string | undefined>(undefined);

const { allCustomFields, getOptionsForSelect } =
  useSalesforcePicklistFieldsForPush();

const picklistOptions = computed(() => getOptionsForSelect(tagsCrmField.value));

const tagsFieldError = computed(() =>
  getFieldErrorMessage(tagsCrmField.value, allCustomFields.value),
);

function handlePartnerTagsSyncToggled(isEnabled: boolean) {
  const feedId = integrationCRM.value?.id;
  if (feedId == null) return;
  iteratively.userToggledPartnerTagsSync({
    feed_id: feedId,
    is_enabled: isEnabled,
  });
}

watch(
  () => trayState.partnerAttributes,
  (mapping) => {
    tagsCrmField.value =
      mapping.find((f) => f.crossbeam_field === 'tags')?.crm_field ?? undefined;
  },
  { immediate: true },
);

watch(
  tagsCrmField,
  (crmField) => {
    if (!crmField) {
      trayState.partnerAttributes = [];
      return;
    }
    trayState.partnerAttributes = [
      { crossbeam_field: 'tags', crm_field: crmField },
    ];
  },
  { immediate: true },
);
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.partner-tags-push-section {
  @apply flex flex-col gap-12;
}
.partner-tags-push-section label {
  @apply block text-neutral-text-strong font-bold text-sm mb-8;
}
</style>
